import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import clsx from 'clsx';
import { func } from 'prop-types';
import { Grid, Checkbox, Popper } from '@nextiva/next-ui-components';
import { SvgCog } from 'components/SvgIcon';
import { useStateWithLocalStorage } from 'utils/state-helpers';
import {
  UserContext,
  actionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

import TableSettings from './TableSettings';
import { ActionBar } from './ActionBar';

const blockName = 'm-table-header';

export const determineWidth = (colLength) => {
  if (colLength === 4 || colLength === 3) {
    return 2;
  }
  return 4;
};

const TableHeader = ({
  onChange = () => {},
  onAddTeam,
  onDeactivate,
  onResetPassword,
  onReactivate,
  sendBulkEmail,
}) => {
  const {
    dispatch,
    userState: {
      sortOrder,
      sortField,
      userList,
      baseUserList,
      tableHeaderCheckBox,
      selectedUserList,
      userFilterStatus,
    },
  } = useContext(UserContext);
  const {
    featureState: { authV3Enabled },
  } = useContext(FeatureContext);

  const [selectCount, setSelectCount] = useState(0);
  const [checkboxState, setCheckboxState] = useState('none');

  const [dynamicColState, setDynamicColState] = useStateWithLocalStorage(
    'userDynamicCol',
    [
      { fieldValue: 'Team', fieldState: 'teamDisplay', fieldType: 'string' },
      { fieldValue: 'Role', fieldState: 'roleDisplay', fieldType: 'string' },
    ],
  );

  useEffect(() => {
    dispatch({
      type: actionTypes.SET_DYNAMIC_COL,
      dynamicColState,
    });
  }, [dynamicColState]);

  useEffect(() => {
    switch (true) {
      // if no users in userList then prevent checked checkbox from showing
      case userList.length > 0 && userList.every((u) => u.checked):
        return setCheckboxState('every');
      case selectedUserList.length === 0:
        return setCheckboxState('none');
      default:
        return setCheckboxState('some');
    }
  }, [userList, selectedUserList]);

  const onChangeHandler = (e) => {
    if (userList.every((u) => u.checked)) {
      dispatch({
        type: actionTypes.SET_TABLE_HEADER_STATE,
        tableHeaderState: false,
      });

      dispatch({
        type: actionTypes.SET_USER_LIST,
        userList: userList.map((u) => ({ ...u, checked: false })),
      });

      dispatch({
        type: actionTypes.SET_BASE_USER_LIST,
        userList: baseUserList.map((u) => ({ ...u, checked: false })),
      });
    } else {
      dispatch({
        type: actionTypes.SET_TABLE_HEADER_STATE,
        tableHeaderState: true,
      });

      dispatch({
        type: actionTypes.SET_USER_LIST,
        userList: userList.map((u) => ({ ...u, checked: true })),
      });

      dispatch({
        type: actionTypes.SET_BASE_USER_LIST,
        userList: baseUserList.map((u) =>
          userList.map((user) => user.email).includes(u.email)
            ? { ...u, checked: true }
            : u,
        ),
      });
    }
    onChange(e);
  };

  const checkBoxList = useRef([]);
  const maxSelectCount = 4;

  const _getCheckBoxListActiveCount = () => {
    if (checkBoxList.current.length === 0) {
      return 0;
    }

    return checkBoxList.current.filter((f) => f.checked).length;
  };

  const _shouldDisable = (isChecked) => {
    if (isChecked) {
      return false;
    }

    const count = _getCheckBoxListActiveCount();

    if (!count) {
      return false;
    }

    return count >= maxSelectCount;
  };

  const _getCheckBoxIndexByValue = (checkBoxValue) => {
    return checkBoxList.current.findIndex((c) => {
      return c.checkBoxValue === checkBoxValue;
    });
  };

  const setCheckboxListStatusByIndex = (index, state) => {
    if (index > -1) {
      checkBoxList.current[index].checked = state;
    }
  };

  const _addCheckBoxToList = (obj) => {
    const checkBoxItem = _getCheckBoxIndexByValue(obj.checkBoxValue);

    if (checkBoxItem > -1) {
      return true;
    }

    checkBoxList.current.push(obj);

    return true;
  };

  const checkBoxStateHelper = useCallback(
    (checkBoxValue, checkBoxField, fieldType, onChangeCallback) => {
      let initState = false;

      if (dynamicColState.find((col) => col.fieldValue === checkBoxValue)) {
        initState = true;
        _addCheckBoxToList({
          checkBoxValue,
          checkBoxField,
          checked: true,
        });
      } else {
        _addCheckBoxToList({
          checkBoxValue,
          checkBoxField,
          checked: false,
        });
      }

      const [value, setState] = useState(initState);

      const onChange = (e) => {
        setState(e.target.checked);
        const index = _getCheckBoxIndexByValue(checkBoxValue);

        if (index > -1) {
          setCheckboxListStatusByIndex(index, e.target.checked);
        } else {
          _addCheckBoxToList({
            checkBoxValue,
            checkBoxField,
            checked: false,
          });
        }
        if (onChangeCallback) {
          onChangeCallback(e, checkBoxValue);
        }

        if (checkBoxValue) {
          if (e.target.checked) {
            const newCols = [
              ...dynamicColState,
              {
                fieldValue: checkBoxValue,
                fieldState: checkBoxField,
                fieldType,
              },
            ];
            setDynamicColState(newCols);
          } else {
            const filteredCols = dynamicColState.filter(
              (col) => col.fieldValue !== checkBoxValue,
            );
            setDynamicColState(filteredCols);
          }
        }
      };

      const count = _getCheckBoxListActiveCount();

      setSelectCount(count);

      return {
        onChange,
        checked: value,
        disabled: _shouldDisable(value),
      };
    },
  );

  const sortNameField = () => {
    dispatch({
      type: actionTypes.UPDATE_TABLE_SORT,
      sortFieldType: 'string',
      sortField: authV3Enabled ? 'firstName' : 'first_name',
    });
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const svgCog = useRef(null);
  const wasOpen = useRef(false);

  const toggleSettings = () => {
    setAnchorEl(anchorEl ? null : svgCog.current);
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
    // guarantee the <Popper /> is unmounted
    // before setting anchorEl is set to null
    setTimeout(() => {
      setAnchorEl(null);
    }, 0);
  };

  useEffect(() => {
    if (tableHeaderCheckBox && open) {
      wasOpen.current = true;
      handleClose();
    } else if (!tableHeaderCheckBox && wasOpen.current) {
      wasOpen.current = false;
      toggleSettings();
    }
  }, [tableHeaderCheckBox]);

  const handleToggle = (isActive) => {
    if (isActive !== userFilterStatus) {
      dispatch({
        type: actionTypes.SET_FILTER_STATUS,
        userFilterStatus: isActive,
      });
      dispatch({
        type: actionTypes.FILTER_USER_LIST,
        authV3Enabled,
      });
    }
  };

  const columns = dynamicColState.map((col) => {
    const sortDynField = () => {
      dispatch({
        type: actionTypes.UPDATE_TABLE_SORT,
        sortFieldType: col.fieldType,
        sortField: col.fieldState,
      });
    };
    return (
      <Grid item xs={determineWidth(dynamicColState.length)}>
        <div
          onClick={sortDynField}
          role="presentation"
          className="table-header-item m-table-header__col"
          data-testid={`PlatformAdminUI-UsersLink-TableHeader-${col.fieldValue}`}
        >
          <span className={clsx('m-table-header__text', 'Heading6')}>
            {col.fieldValue}
          </span>
          <span
            className={clsx(
              'm-table-header__text',
              'sort_icon',
              sortField === col.fieldState && `${sortOrder}_icon`,
            )}
            data-testid={`PlatformAdminUI-UsersLink-TableHeader-${col.fieldValue}-${sortOrder}-icon`}
          />
        </div>
      </Grid>
    );
  });

  return (
    <div
      className={clsx(
        tableHeaderCheckBox && 'm-table-header__actionbar',
        'm-table-header__actionbar-cols',
        blockName,
      )}
    >
      <div
        className="m-table-header__checkbox"
        role="presentation"
        onClick={onChangeHandler}
        data-testid="PlatformAdminUI-UsersLink-TableHeader-Presentation"
      >
        <Checkbox
          checked={checkboxState === 'some' || checkboxState === 'every'}
          indeterminate={checkboxState === 'some'}
          data-testid="PlatformAdminUI-UsersLink-TableHeader-CheckBox"
        />
      </div>
      {!tableHeaderCheckBox && (
        <Grid container alignItems="center">
          <Grid item xs={3}>
            <div
              onClick={sortNameField}
              role="presentation"
              className="table-header-item m-table-header__col"
              data-testid="PlatformAdminUI-UsersLink-TableHeader-Name"
            >
              <span className={clsx('m-table-header__text', 'Heading6')}>
                Name
              </span>
              <span
                className={clsx(
                  'm-table-header__text',
                  'sort_icon',
                  (sortField === 'first_name' || sortField === 'firstName') &&
                    `${sortOrder}_icon`,
                )}
                data-testid={`PlatformAdminUI-UsersLink-TableHeader-Name-${sortOrder}-icon`}
              ></span>
            </div>
          </Grid>
          {columns}
          <Grid item xs={1}>
            <div
              role="presentation"
              className="m-table-header__settings m-table-header__settings__icon"
              onClick={toggleSettings}
              ref={svgCog}
              data-testid="PlatformAdminUI-UsersLink-TableHeader-SettingsIcon"
            >
              <SvgCog />
            </div>
            <Popper
              id="popoverGear"
              open={open}
              anchorEl={anchorEl}
              disablePortal
              placement="bottom"
              style={{ zIndex: '999' }}
            >
              <TableSettings
                checkBoxStateHelper={checkBoxStateHelper}
                handleToggle={handleToggle}
                selectCount={selectCount}
                handleClose={handleClose}
                userFilterStatus={userFilterStatus}
              />
            </Popper>
          </Grid>
        </Grid>
      )}
      {tableHeaderCheckBox && (
        <ActionBar
          onAddTeam={onAddTeam}
          onDeactivate={onDeactivate}
          onResetPassword={onResetPassword}
          onReactivate={onReactivate}
          sendBulkEmail={sendBulkEmail}
          data-testid="PlatformAdminUI-TableHeader-ActionBar"
        />
      )}
    </div>
  );
};

TableHeader.propTypes = {
  sendBulkEmail: func,
  onAddTeam: func,
  onChange: func,
  onDeactivate: func,
  onReactivate: func,
  onResetPassword: func,
};

export default TableHeader;
